import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageSquare, Mail, Phone, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactModal = ({ isOpen, onClose, source, leadInfo = null, onSuccess }) => {
  const [formData, setFormData] = useState({
    first_name: leadInfo?.first_name || '',
    email: leadInfo?.email || '',
    phone: leadInfo?.phone || '',
    preferred_contact: 'text',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        source: source || 'contact_modal',
        lead_id: leadInfo?.id
      });

      setShowSuccess(true);
      
      if (onSuccess) {
        onSuccess(formData);
      }

      setTimeout(() => {
        setShowSuccess(false);
        onClose();
      }, 3000);
    } catch (error) {
      // Error handled by UI state
      alert('Failed to submit. Please try again or contact Bryan directly.');
      setIsSubmitting(false);
    }
  };

  const handleTextBryan = () => {
    window.location.href = `sms:8016518363?body=Hi Bryan, I'd like to discuss my weight loss options.`;
  };

  const handleEmailBryan = () => {
    window.location.href = `mailto:Bryan@DirectMeds.com?subject=Weight Loss Options&body=Hi Bryan,%0A%0AI'd like to learn more about my options.`;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-lg bg-slate-950/95 backdrop-blur-xl border border-cyan-500/20 rounded-2xl shadow-[0_0_50px_rgba(34,211,238,0.3)] overflow-hidden"
          data-testid="contact-modal"
        >
          {showSuccess ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-emerald-400" />
              </div>
              <h3 className="font-heading text-2xl font-bold mb-2 text-white">Message Sent!</h3>
              <p className="text-slate-400 mb-6">Bryan will reach out to you shortly.</p>
              
              <div className="flex gap-3 justify-center">
                <button
                  onClick={handleTextBryan}
                  className="flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/20 rounded-lg text-cyan-300 transition-all"
                >
                  <MessageSquare className="h-4 w-4" />
                  <span className="text-sm">Text Bryan</span>
                </button>
                <button
                  onClick={handleEmailBryan}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 hover:bg-slate-700 rounded-lg text-slate-300 transition-all"
                >
                  <Mail className="h-4 w-4" />
                  <span className="text-sm">Email Bryan</span>
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between p-6 border-b border-cyan-500/20 bg-gradient-to-r from-cyan-500/10 to-transparent">
                <div>
                  <h3 className="font-heading text-2xl font-bold text-white">Contact Bryan</h3>
                  <p className="text-sm text-cyan-400">Get personalized guidance on your options</p>
                </div>
                <button
                  onClick={onClose}
                  className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
                >
                  <X className="h-5 w-5 text-slate-400 hover:text-white" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <Label className="text-slate-400 text-sm mb-2 block">First Name *</Label>
                  <Input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    className="bg-slate-900/60 border-slate-800 focus:border-cyan-500 h-11 text-white"
                    required
                    data-testid="contact-name"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-400 text-sm mb-2 block">Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="bg-slate-900/60 border-slate-800 focus:border-cyan-500 h-11 text-white"
                      data-testid="contact-email"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm mb-2 block">Phone *</Label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="bg-slate-900/60 border-slate-800 focus:border-cyan-500 h-11 text-white"
                      required
                      data-testid="contact-phone"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-slate-400 text-sm mb-2 block">Preferred Contact Method</Label>
                  <div className="flex gap-3">
                    {['text', 'call', 'email'].map((method) => (
                      <button
                        key={method}
                        type="button"
                        onClick={() => setFormData({...formData, preferred_contact: method})}
                        className={`flex-1 py-2.5 rounded-lg border capitalize text-sm transition-all ${
                          formData.preferred_contact === method
                            ? 'border-cyan-500 bg-cyan-500/10 text-cyan-300'
                            : 'border-slate-800 bg-slate-900/30 text-slate-400 hover:border-slate-700'
                        }`}
                      >
                        {method}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-slate-400 text-sm mb-2 block">Message (Optional)</Label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    placeholder="Any questions or details you'd like to share..."
                    rows={3}
                    className="w-full bg-slate-900/60 border border-slate-800 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 rounded-lg p-3 text-sm text-white placeholder-slate-500 resize-none"
                    data-testid="contact-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 text-white rounded-xl py-6 text-base font-bold h-auto"
                  data-testid="contact-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Phone className="mr-2 h-5 w-5" />
                      Send Message to Bryan
                    </>
                  )}
                </Button>

                <p className="text-xs text-slate-500 text-center">
                  Bryan typically responds within a few hours during business hours
                </p>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ContactModal;
